/**
 * Edit record modal — the record detail surface (there is no detail route,
 * matching loop). Headline + body editing, type conversion (server
 * migrates the date axis), due-date change, deliver (feedback), delete,
 * and the Replies thread (children via parentRecordId + a nested capture
 * bar).
 *
 * Loop content is the verbatim question in the asker's voice — headline
 * and body are locked; close it with an answer instead.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Button, Dialog, toast, useConfirm } from "@/ui";
import {
    loopRecords, loopVerbs, type LoopRecord, type RecordLinkTarget, type RecordType,
} from "../../lib/api";
import { buildDetailsDoc, docToPlainText } from "../../lib/grammar";
import CaptureBar from "./CaptureBar";
import Typeahead from "../Typeahead";
import LinkedItems from "../LinkedItems";
import { useLoopDirectory, useTargetNames } from "./useDirectory";

const TYPE_HINT: Record<RecordType, string> = {
    action:   "to-do with a due date · can go overdue",
    reminder: "soft nudge on a date · doesn't go overdue",
    loop:     "something you owe a contact — close it with the answer",
    ask:      "bring up with a contact next time you talk",
    decision: "captured decision · no date",
    note:     "freeform note · no date",
    feedback: "delivery-tracked feedback for a person",
};

const TYPE_LABEL: Record<RecordType, string> = {
    note: "note", feedback: "feedback", decision: "decision",
    action: "action", loop: "owe", reminder: "reminder", ask: "bring up",
};

export default function EditRecordModal({
    record,
    open,
    onOpenChange,
}: {
    record: LoopRecord;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const { directory } = useLoopDirectory();

    const [content, setContent] = useState(record.content);
    const [details, setDetails] = useState(() => docToPlainText(record.details));
    const [dueDate, setDueDate] = useState(record.dueDate ?? "");
    const assigneeKey = (r: { assigneeContactId: string | null; assigneeCompanyId: string | null }) =>
        r.assigneeContactId ? `contact:${r.assigneeContactId}` : r.assigneeCompanyId ? `company:${r.assigneeCompanyId}` : "";
    const [assignee, setAssignee] = useState(() => assigneeKey(record));
    // Re-sync local state when a different record opens or the type flips
    // server-side (changeType round-trip).
    useEffect(() => {
        setContent(record.content);
        setDetails(docToPlainText(record.details));
        setDueDate(record.dueDate ?? "");
        setAssignee(assigneeKey(record));
    }, [record.id, record.type, record.content, record.details, record.dueDate, record.assigneeContactId, record.assigneeCompanyId]);

    const isLoop = record.type === "loop";
    const dated = record.type === "action" || record.type === "loop" || record.type === "ask";

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ["loop"] });

    const save = useMutation({
        mutationFn: async () => {
            const contentChanged = content.trim() !== record.content || details !== docToPlainText(record.details);
            if (contentChanged && !isLoop) {
                const res = await loopRecords.updateContent(record.id, content.trim(), buildDetailsDoc(details));
                if (!res.ok) throw new Error(res.error);
            }
            const dueChanged = dated && (dueDate || null) !== (record.dueDate ?? null);
            if (dueChanged && record.status === "open") {
                const res = await loopRecords.later(record.id, dueDate || null);
                if (!res.ok) throw new Error(res.error);
            }
            if (assignee !== assigneeKey(record)) {
                const [kind, ...rest] = assignee.split(":");
                const id = rest.join(":") || null;
                const res = await loopRecords.assign(record.id, kind === "contact" ? id : null, kind === "company" ? id : null);
                if (!res.ok) throw new Error(res.error);
            }
        },
        onSuccess: () => { invalidate(); onOpenChange(false); },
        onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Save failed"),
    });

    const changeType = useMutation({
        mutationFn: async (type: RecordType) => {
            const res = await loopVerbs.changeType(record.id, type);
            if (!res.ok) throw new Error(res.error);
        },
        onSuccess: invalidate,
        onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Couldn't change type"),
    });

    const deliver = useMutation({
        mutationFn: async () => {
            const res = await loopVerbs.deliver(record.id);
            if (!res.ok) throw new Error(res.error);
        },
        onSuccess: invalidate,
        onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Couldn't mark delivered"),
    });

    const remove = useMutation({
        mutationFn: async () => {
            const res = await loopVerbs.delete([record.id]);
            if (!res.ok) throw new Error(res.error);
        },
        onSuccess: () => { invalidate(); onOpenChange(false); },
        onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Delete failed"),
    });

    const childrenQuery = useQuery({
        queryKey: ["loop", "children", record.id],
        queryFn: () => loopVerbs.children(record.id),
        enabled: open,
    });
    const replies = childrenQuery.data?.ok ? childrenQuery.data.records : [];

    // ── `#` attachments (meetings / accounts / pipeline / events) ─────────
    const targetName = useTargetNames();
    const linksQuery = useQuery({
        queryKey: ["loop", "links", record.id],
        queryFn: () => loopRecords.links([record.id]),
        enabled: open,
    });
    const currentLinks = (linksQuery.data?.ok ? linksQuery.data.links : [])
        .map((l) => ({ targetType: l.targetType, targetId: l.targetId }));
    const setLinks = useMutation({
        mutationFn: async (links: Array<{ targetType: RecordLinkTarget; targetId: string }>) => {
            const res = await loopRecords.setLinks(record.id, links);
            if (!res.ok) throw new Error(res.error);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["loop", "links", record.id] });
            // Row chips on list pages read from the page-level batch query.
            queryClient.invalidateQueries({ queryKey: ["loop", "links"] });
        },
        onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Couldn't update attachments"),
    });
    const attachedIds = new Set(currentLinks.map((l) => l.targetId));

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Panel className="loop-edit-modal">
                <Dialog.Title render={<h3 style={{ marginTop: 0, marginBottom: 4 }} />}>
                    Edit {TYPE_LABEL[record.type]}
                </Dialog.Title>

                <label className="loop-edit-field">
                    <span className="muted">Type</span>
                    <select
                        value={record.type}
                        disabled={changeType.isPending}
                        onChange={(e) => changeType.mutate(e.target.value as RecordType)}
                    >
                        {(Object.keys(TYPE_HINT) as RecordType[]).map((t) => (
                            <option key={t} value={t}>/{t} — {TYPE_HINT[t]}</option>
                        ))}
                    </select>
                </label>

                <label className="loop-edit-field">
                    <span className="muted">Headline</span>
                    <input
                        maxLength={280}
                        value={content}
                        disabled={isLoop}
                        onChange={(e) => setContent(e.target.value)}
                    />
                </label>
                {isLoop && (
                    <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
                        Loop content is the verbatim question and can't be edited — close the loop with an answer instead.
                    </p>
                )}

                <label className="loop-edit-field">
                    <span className="muted">Notes</span>
                    <textarea
                        rows={4}
                        value={details}
                        disabled={isLoop}
                        placeholder="Optional details. Leave empty for a headline-only record."
                        onChange={(e) => setDetails(e.target.value)}
                    />
                </label>

                <div className="row" style={{ gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                    {dated && record.status === "open" && (
                        <label className="loop-edit-field" style={{ maxWidth: 220 }}>
                            <span className="muted">Due date</span>
                            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                        </label>
                    )}
                    <label className="loop-edit-field" style={{ minWidth: 240, flex: 1 }}>
                        <span className="muted">Assignee</span>
                        <Typeahead
                            value={assignee}
                            emptyLabel="Me"
                            placeholder="Me"
                            options={[
                                ...directory.contacts.filter((c) => c.id !== "me").map((c) => ({ id: `contact:${c.id}`, label: c.name, group: "Contacts" })),
                                ...directory.companies.map((c) => ({ id: `company:${c.id}`, label: c.name, group: "Accounts" })),
                            ]}
                            onChange={setAssignee}
                        />
                    </label>
                </div>
                {record.type === "reminder" && (
                    <p className="muted" style={{ fontSize: 12 }}>
                        Fires {record.fireDate ?? "—"}{record.recurrence !== "none" ? ` · repeats ${record.recurrence}` : ""} — use Snooze on the row to push it.
                    </p>
                )}
                {record.type === "feedback" && (
                    <div className="row" style={{ gap: 8, alignItems: "center", marginBottom: 8 }}>
                        <span className={`loop-chip ${record.delivered ? "" : "loop-chip--redate"}`}>
                            {record.delivered ? "delivered" : "undelivered"}
                        </span>
                        {!record.delivered && (
                            <Button size="sm" variant="ghost" disabled={deliver.isPending} onClick={() => deliver.mutate()}>
                                Mark delivered
                            </Button>
                        )}
                    </div>
                )}
                {record.answer && (
                    <p className="muted" style={{ fontSize: 13 }}>↳ Answer: {record.answer}</p>
                )}

                <div className="loop-edit-attachments">
                    <h4 style={{ margin: "12px 0 6px", fontSize: 13 }}>Attached to</h4>
                    <div className="row" style={{ gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        {currentLinks.map((l) => (
                            <span key={`${l.targetType}:${l.targetId}`} className="loop-chip loop-chip--link" style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                                #{targetName(l.targetType, l.targetId) ?? l.targetType}
                                <button type="button" className="loop-chip-x" title="Detach"
                                    disabled={setLinks.isPending}
                                    onClick={() => setLinks.mutate(currentLinks.filter((c) => c.targetId !== l.targetId || c.targetType !== l.targetType))}>
                                    <X size={11} />
                                </button>
                            </span>
                        ))}
                        <span style={{ minWidth: 220 }}>
                            <Typeahead
                                value=""
                                placeholder="Attach meeting, account, deal…"
                                emptyLabel="None"
                                options={directory.targets
                                    .filter((t) => t.kind !== "contact" && !attachedIds.has(t.id))
                                    .map((t) => ({ id: `${t.kind}:${t.id}`, label: t.name, sublabel: t.kind }))}
                                onChange={(picked) => {
                                    if (!picked) return;
                                    const [kind, ...rest] = picked.split(":");
                                    setLinks.mutate([...currentLinks, { targetType: kind as RecordLinkTarget, targetId: rest.join(":") }]);
                                }}
                            />
                        </span>
                    </div>
                </div>

                <div className="loop-edit-attachments"><LinkedItems type="record" id={record.id} /></div>

                <div className="loop-edit-replies">
                    <h4 style={{ margin: "12px 0 6px", fontSize: 13 }}>Replies · {replies.length}</h4>
                    {replies.map((r) => (
                        <div key={r.id} className="loop-edit-reply">
                            <span className={`loop-type-badge loop-type--${r.type}`}>{r.type}</span>
                            <span className={r.status !== "open" ? "loop-content--done" : ""}>{r.content}</span>
                        </div>
                    ))}
                    <CaptureBar
                        directory={directory}
                        parentRecordId={record.id}
                        placeholder="/note reply — threads under this record"
                    />
                </div>

                <div className="row" style={{ gap: 8, marginTop: 16 }}>
                    <Button variant="danger" disabled={remove.isPending}
                        onClick={async () => {
                            if (await confirm({
                                title: "Delete this record?",
                                description: `“${record.content.slice(0, 90)}” — this can't be undone from the app.`,
                                confirmLabel: "Delete",
                            })) remove.mutate();
                        }}>
                        Delete
                    </Button>
                    <span className="spacer" />
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button variant="primary" disabled={save.isPending || (!isLoop && !content.trim())}
                        onClick={() => save.mutate()}>
                        Save
                    </Button>
                </div>
            </Dialog.Panel>
        </Dialog.Root>
    );
}
